import { friendlyCrmSyncError } from "@/lib/crmSyncError";
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { PropertyAvailabilityTab } from "@/modules/availability/PropertyAvailabilityTab";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { Star, ExternalLink, Lock } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { CopyButton } from "@/components/atoms/CopyButton";
import { DataTable, type Column } from "@/components/organisms/DataTable";
import { EmptyState } from "@/components/molecules/EmptyState";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Drawer } from "@/components/organisms/Drawer";
import { ImageViewer } from "@/components/organisms/ImageViewer";
import { ContactDetailDrawer } from "@/modules/contacts/ContactDetailDrawer";
import { useResource } from "@/hooks/useResource";
import {
  buildOwnerSelectOptions,
  contactDetailsToSummary,
} from "@/lib/contactDisplay";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import { formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { crmDisplayName } from "@/lib/syncAlto";
import { branchesService } from "@/services/branchesService";
import { contactsService } from "@/services/contactsService";
import { documentsService } from "@/services/documentsService";
import { mediaAssetsService } from "@/services/mediaAssetsService";
import { propertiesService } from "@/services/propertiesService";
import { usersService } from "@/services/usersService";
import { getApiErrorMessage } from "@/utils/apiErrors";
import type {
  BranchSummary,
  ContactSummary,
  PropertyDetail,
  UserSummary,
  DocumentSummary,
} from "@/types/entities";
import type { PropertyMediaAssetEntity } from "@/types/property";
import { useSettingsStore } from "@/store/settingsStore";
import { PropertyFormFields } from "./PropertyFormFields";
// import { FileNoteModal } from "@/modules/contacts/FileNoteModal";
import {
  buildPropertyUpdatePatch,
  emptyPropertyFormValues,
  propertyDetailToFormValues,
  propertyListingReadOnlyFromDetail,
  validatePropertyForm,
  type PropertyFormValues,
} from "./propertyFormUtils";

const PROPERTY_TABS = [
  "Overview",
  // "Availability",
  "Timeline",
  "Rooms",
  "Media",
  "Documents",
  "Mapping",
  "Matches",
  "Keys",
  "Appraisal",
] as const;

type PropertyTab = (typeof PROPERTY_TABS)[number];

interface Props {
  open: boolean;
  propertyId: string | null;
  onClose: () => void;
  onSaved?: () => void;
  /**
   * Alto sync status from the list row. `GET /properties/{id}` omits `syncAlto`,
   * so the status has to be handed down from `GET /properties`.
   */
  crmSyncStatus?: string | null;
  syncAlto?: string | null;
}

function ownerIdsFromDetail(
  data: PropertyDetail,
  contactsPool: ContactSummary[] = [],
): string[] {
  const ids = (data.owners ?? [])
    .map((o) => {
      const atlasContactId = o.contactId?.trim();
      if (atlasContactId) return atlasContactId;
      const externalContactId = o.externalContactId?.trim();
      if (!externalContactId) return "";
      const matched = contactsPool.find(
        (contact) => contact.externalId?.trim() === externalContactId,
      );
      return matched?.id?.trim() ?? "";
    })
    .filter((id): id is string => Boolean(id));
  return ids.length > 0 ? ids : [""];
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

interface GalleryImageViewItem {
  id: string;
  order: number | null;
  isMain: boolean;
  modifiedAt: string | null;
  thumbnailUrl: string;
  screenUrl: string;
}

const MEDIA_PAGE_SIZE = 30;
const DOCUMENTS_PAGE_SIZE = 30;
const NEGOTIATOR_DROPDOWN_PAGE_SIZE = 20;

const PROPERTY_DOCUMENT_COLUMNS: Column<DocumentSummary>[] = [
  {
    id: "name",
    header: "Document",
    cell: (r) => (
      <div className="min-w-0 flex items-center gap-2">
        {r.isPrivate ? <Lock size={12} className="text-bridge-muted shrink-0" /> : null}
        <div className="min-w-0">
          <div className="truncate font-medium text-bridge-fg">{r.name ?? "(no name)"}</div>
          <div className="truncate text-xs text-bridge-muted">{r.caption ?? r.fileType ?? ""}</div>
        </div>
      </div>
    ),
  },
  {
    id: "fileType",
    header: "Type",
    cell: (r) => r.fileType ?? <span className="text-bridge-subtle">—</span>,
  },
  {
    id: "associatedType",
    header: "Linked",
    cell: (r) => r.associatedType ?? <span className="text-bridge-subtle">—</span>,
  },
  {
    id: "isPrivate",
    header: "Visibility",
    cell: (r) =>
      r.isPrivate ? (
        <Badge tone="warning">Private</Badge>
      ) : (
        <Badge tone="neutral">Public</Badge>
      ),
  },
  {
    id: "url",
    header: "Open",
    cell: (r) =>
      r.url ? (
        <a
          href={r.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-bridge-accentSoft hover:text-bridge-accent"
          onClick={(e) => e.stopPropagation()}
        >
          Open <ExternalLink size={12} />
        </a>
      ) : (
        <span className="text-bridge-subtle">—</span>
      ),
  },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    align: "right",
    cell: (r) => (
      <span className="text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>
    ),
  },
];

export function PropertyDetailDrawer({
  open,
  propertyId,
  onClose,
  onSaved,
  crmSyncStatus,
  syncAlto,
}: Props) {
  const { legalEntityId, branchId } = useSettingsStore();
  const [activeTab, setActiveTab] = useState<PropertyTab>("Overview");
  const [isSaving, setIsSaving] = useState(false);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [negotiators, setNegotiators] = useState<UserSummary[]>([]);
  const [negotiatorsPage, setNegotiatorsPage] = useState(-1);
  const [negotiatorsHasNext, setNegotiatorsHasNext] = useState(true);
  const [negotiatorsLoaded, setNegotiatorsLoaded] = useState(false);
  const [resolvingNegotiator, setResolvingNegotiator] = useState(false);
  const [loadingNegotiators, setLoadingNegotiators] = useState(false);
  const [ownerContactIds, setOwnerContactIds] = useState<string[]>([""]);
  const [contactsPool, setContactsPool] = useState<ContactSummary[]>([]);
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [ownerCreateOpen, setOwnerCreateOpen] = useState(false);
  const [mediaAssets, setMediaAssets] = useState<PropertyMediaAssetEntity[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaLoadingMore, setMediaLoadingMore] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mediaPage, setMediaPage] = useState(0);
  const [mediaHasNext, setMediaHasNext] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState<number | null>(null);
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsLoadingMore, setDocumentsLoadingMore] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [documentsPage, setDocumentsPage] = useState(0);
  const [documentsHasNext, setDocumentsHasNext] = useState(false);
  // const [fileNoteOpen, setFileNoteOpen] = useState(false);
  const loadedRef = useRef<PropertyDetail | null>(null);
  const baselineOwnersRef = useRef<string>("");
  const contactsFetchGen = useRef(0);
  const mediaFetchGen = useRef(0);
  const documentsFetchGen = useRef(0);
  const mediaFetchInFlightRef = useRef(false);
  const documentsFetchInFlightRef = useRef(false);
  const ownerContactIdsRef = useRef(ownerContactIds);
  const hydratedOwnerIdsRef = useRef(new Set<string>());
  const hydratedOwnerExternalIdsRef = useRef(new Set<string>());
  const negotiatorHydrationGen = useRef(0);
  const negotiatorsFetchGen = useRef(0);
  ownerContactIdsRef.current = ownerContactIds;

  const { data, loading, error, reload } = useResource(
    () =>
      propertyId ? propertiesService.getOne(propertyId) : Promise.resolve(null),
    [propertyId, open],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<PropertyFormValues>({
    mode: "onTouched",
    defaultValues: emptyPropertyFormValues(),
  });

  const mergeById = <T extends { id: string }>(prev: T[], next: T[]): T[] => {
    const map = new Map(prev.map((x) => [x.id, x]));
    for (const x of next) map.set(x.id, x);
    return [...map.values()];
  };

  const fetchNegotiatorsPage = useCallback(
    async (page: number) => {
      if (loadingNegotiators) return;
      const gen = ++negotiatorsFetchGen.current;
      const scopeLegalEntityId =
        data?.legalEntityId?.trim() || legalEntityId || undefined;
      setLoadingNegotiators(true);
      try {
        const res = await usersService.listNegotiators(
          { legalEntityId: scopeLegalEntityId },
          { page, size: NEGOTIATOR_DROPDOWN_PAGE_SIZE },
        );
        if (gen !== negotiatorsFetchGen.current) return;
        setNegotiators((prev) => mergeById(prev, res.content ?? []));
        setNegotiatorsPage(page);
        setNegotiatorsHasNext(Boolean(res.hasNext));
        setNegotiatorsLoaded(true);
      } catch (err) {
        console.error("Failed to load negotiators", err);
      } finally {
        if (gen === negotiatorsFetchGen.current) setLoadingNegotiators(false);
      }
    },
    [data?.legalEntityId, legalEntityId, loadingNegotiators],
  );

  const handleNegotiatorOpen = useCallback(() => {
    if (!negotiatorsLoaded && !loadingNegotiators) void fetchNegotiatorsPage(0);
  }, [fetchNegotiatorsPage, loadingNegotiators, negotiatorsLoaded]);

  const handleNegotiatorEndReached = useCallback(() => {
    if (loadingNegotiators || !negotiatorsHasNext) return;
    void fetchNegotiatorsPage(negotiatorsPage + 1);
  }, [fetchNegotiatorsPage, loadingNegotiators, negotiatorsHasNext, negotiatorsPage]);

  const fetchContacts = useCallback(
    (trimmedSearch: string) => {
      const gen = ++contactsFetchGen.current;
      const q = trimmedSearch.trim();
      setContactSearchQuery(q);
      setLoadingContacts(true);
      contactsService
        .list(
          {
            ...contactPropertyListScope(legalEntityId, branchId),
            ...(q ? { search: q } : {}),
          },
          { page: 0, size: 200 },
        )
        .then((res) => {
          if (gen !== contactsFetchGen.current) return;
          const items = res.content ?? [];
          setContactsPool((prev) => {
            const pinned = prev.filter((c) =>
              ownerContactIdsRef.current.includes(c.id),
            );
            return q ? mergeById(prev, items) : mergeById(pinned, items);
          });
        })
        .catch((err) => console.error("Failed to load contacts", err))
        .finally(() => {
          if (gen !== contactsFetchGen.current) return;
          setLoadingContacts(false);
        });
    },
    [legalEntityId, branchId],
  );

  const fetchMediaPage = useCallback(
    async (pageToFetch: number, append: boolean, generation: number) => {
      if (!propertyId || mediaFetchInFlightRef.current) return;
      mediaFetchInFlightRef.current = true;

      if (append) {
        setMediaLoadingMore(true);
      } else {
        setMediaLoading(true);
        setMediaError(null);
      }

      try {
        const response = await mediaAssetsService.list(
          {
            propertyId,
            legalEntityId: legalEntityId ?? undefined,
            branchId: branchId ?? undefined,
          },
          { page: pageToFetch, size: MEDIA_PAGE_SIZE, sort: "order", direction: "asc" },
        );

        if (generation !== mediaFetchGen.current) return;

        setMediaAssets((prev) =>
          append ? [...prev, ...(response.content ?? [])] : (response.content ?? []),
        );
        setMediaPage(response.page ?? pageToFetch);
        setMediaHasNext(Boolean(response.hasNext));
      } catch (err) {
        if (generation !== mediaFetchGen.current) return;
        if (!append) setMediaAssets([]);
        setMediaError(getApiErrorMessage(err) ?? "Failed to load property media");
      } finally {
        mediaFetchInFlightRef.current = false;
        if (generation !== mediaFetchGen.current) return;
        if (append) {
          setMediaLoadingMore(false);
        } else {
          setMediaLoading(false);
        }
      }
    },
    [propertyId, legalEntityId, branchId],
  );

  const fetchDocumentsPage = useCallback(
    async (
      pageToFetch: number,
      append: boolean,
      generation: number,
      externalPropertyId: string | null | undefined,
    ) => {
      if (!propertyId || !externalPropertyId?.trim() || documentsFetchInFlightRef.current) return;
      documentsFetchInFlightRef.current = true;

      if (append) {
        setDocumentsLoadingMore(true);
      } else {
        setDocumentsLoading(true);
        setDocumentsError(null);
      }

      try {
        const response = await documentsService.list(
          {
            externalPropertyId: externalPropertyId.trim(),
            legalEntityId: legalEntityId ?? undefined,
            branchId: branchId ?? undefined,
          },
          {
            page: pageToFetch,
            size: DOCUMENTS_PAGE_SIZE,
            sort: "atlasModifiedAt",
            direction: "desc",
          },
        );

        if (generation !== documentsFetchGen.current) return;

        setDocuments((prev) =>
          append ? [...prev, ...(response.content ?? [])] : (response.content ?? []),
        );
        setDocumentsPage(response.page ?? pageToFetch);
        setDocumentsHasNext(Boolean(response.hasNext));
      } catch (err) {
        if (generation !== documentsFetchGen.current) return;
        if (!append) setDocuments([]);
        setDocumentsError(getApiErrorMessage(err) ?? "Failed to load property documents");
      } finally {
        documentsFetchInFlightRef.current = false;
        if (generation !== documentsFetchGen.current) return;
        if (append) {
          setDocumentsLoadingMore(false);
        } else {
          setDocumentsLoading(false);
        }
      }
    },
    [propertyId, legalEntityId, branchId],
  );

  useEffect(() => {
    if (!open) return;
    setActiveTab("Overview");
    branchesService
      .list({}, { page: 0, size: 200 })
      .then((r) => setBranches(r.content || []))
      .catch(() => setBranches([]));
    fetchContacts("");
  }, [open, fetchContacts]);

  useEffect(() => {
    if (!data) return;
    loadedRef.current = data;
    const owners = ownerIdsFromDetail(data);
    baselineOwnersRef.current = owners.filter(Boolean).join("|");
    setOwnerContactIds(owners);
    reset(propertyDetailToFormValues(data));
  }, [data, reset]);

  useEffect(() => {
    if (!open || !data?.userId?.trim()) return;

    const gen = ++negotiatorHydrationGen.current;
    const scopeLegalEntityId =
      data.legalEntityId?.trim() || legalEntityId || undefined;
    setResolvingNegotiator(true);

    void usersService
      .lookupNegotiator(scopeLegalEntityId, data.userId, data.externalUserId)
      .then((user) => {
        if (gen !== negotiatorHydrationGen.current || !user?.id) return;
        setNegotiators((prev) => mergeById(prev, [user]));
        setValue("negotiatorId", user.id, { shouldDirty: false });
      })
      .finally(() => {
        if (gen === negotiatorHydrationGen.current) setResolvingNegotiator(false);
      });
  }, [open, data, legalEntityId, setValue]);

  useEffect(() => {
    if (!open) {
      loadedRef.current = null;
      baselineOwnersRef.current = "";
      contactsFetchGen.current += 1;
      mediaFetchGen.current += 1;
      documentsFetchGen.current += 1;
      mediaFetchInFlightRef.current = false;
      documentsFetchInFlightRef.current = false;
      setContactsPool([]);
      setContactSearchQuery("");
      setMediaAssets([]);
      setMediaError(null);
      setMediaLoading(false);
      setMediaLoadingMore(false);
      setMediaPage(0);
      setMediaHasNext(false);
      setMediaViewerIndex(null);
      setDocuments([]);
      setDocumentsError(null);
      setDocumentsLoading(false);
      setDocumentsLoadingMore(false);
      setDocumentsPage(0);
      setDocumentsHasNext(false);
      hydratedOwnerExternalIdsRef.current.clear();
      negotiatorHydrationGen.current += 1;
      negotiatorsFetchGen.current += 1;
      setNegotiators([]);
      setNegotiatorsPage(-1);
      setNegotiatorsHasNext(true);
      setNegotiatorsLoaded(false);
      setResolvingNegotiator(false);
      setLoadingNegotiators(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || activeTab !== "Media" || !propertyId) return;
    mediaFetchGen.current += 1;
    const generation = mediaFetchGen.current;
    setMediaAssets([]);
    setMediaError(null);
    setMediaPage(0);
    setMediaHasNext(false);
    setMediaLoadingMore(false);
    void fetchMediaPage(0, false, generation);
  }, [open, activeTab, propertyId, fetchMediaPage]);

  useEffect(() => {
    if (!open || activeTab !== "Documents" || !propertyId || !data?.externalId?.trim()) return;
    documentsFetchGen.current += 1;
    const generation = documentsFetchGen.current;
    setDocuments([]);
    setDocumentsError(null);
    setDocumentsPage(0);
    setDocumentsHasNext(false);
    setDocumentsLoadingMore(false);
    void fetchDocumentsPage(0, false, generation, data.externalId);
  }, [open, activeTab, propertyId, data?.externalId, fetchDocumentsPage]);

  const handleContentScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (remaining > 260) return;

      if (activeTab === "Media") {
        if (!mediaHasNext || mediaLoading || mediaLoadingMore || mediaFetchInFlightRef.current) return;
        void fetchMediaPage(mediaPage + 1, true, mediaFetchGen.current);
        return;
      }

      if (activeTab === "Documents") {
        if (
          !documentsHasNext ||
          documentsLoading ||
          documentsLoadingMore ||
          documentsFetchInFlightRef.current
        ) {
          return;
        }
        void fetchDocumentsPage(
          documentsPage + 1,
          true,
          documentsFetchGen.current,
          data?.externalId,
        );
      }
    },
    [
      activeTab,
      mediaHasNext,
      mediaLoading,
      mediaLoadingMore,
      mediaPage,
      fetchMediaPage,
      documentsHasNext,
      documentsLoading,
      documentsLoadingMore,
      documentsPage,
      fetchDocumentsPage,
      data?.externalId,
    ],
  );

  const hasChanges = isDirty;

  const ownerOptions = useMemo(
    () =>
      buildOwnerSelectOptions(
        contactsPool,
        ownerContactIds,
        contactSearchQuery,
      ),
    [contactsPool, ownerContactIds, contactSearchQuery],
  );

  useEffect(() => {
    if (!open) {
      hydratedOwnerIdsRef.current.clear();
      hydratedOwnerExternalIdsRef.current.clear();
      return;
    }
    const missing = ownerContactIds
      .map((id) => id.trim())
      .filter(
        (id) =>
          id &&
          !contactsPool.some((c) => c.id === id) &&
          !hydratedOwnerIdsRef.current.has(id),
      );
    for (const id of missing) {
      hydratedOwnerIdsRef.current.add(id);
      contactsService
        .getOne(id, legalEntityId ?? undefined)
        .then((raw) => {
          setContactsPool((prev) =>
            mergeById(prev, [contactDetailsToSummary(raw, id)]),
          );
        })
        .catch(() => {
          hydratedOwnerIdsRef.current.delete(id);
        });
    }
  }, [open, ownerContactIds, contactsPool, legalEntityId]);

  useEffect(() => {
    if (!open || !data) return;
    // Do not override after user manually picks owners.
    if (!ownerContactIds.every((id) => !id.trim())) return;

    const unresolvedExternalIds = (data.owners ?? [])
      .map((owner) => owner.externalContactId?.trim() ?? "")
      .filter(Boolean);
    if (unresolvedExternalIds.length === 0) return;

    const scope = contactPropertyListScope(legalEntityId, branchId);

    unresolvedExternalIds.forEach((externalContactId) => {
      const alreadyInPool = contactsPool.some(
        (contact) => contact.externalId?.trim() === externalContactId,
      );
      if (alreadyInPool) {
        setOwnerContactIds(ownerIdsFromDetail(data, contactsPool));
        return;
      }
      if (hydratedOwnerExternalIdsRef.current.has(externalContactId)) return;
      hydratedOwnerExternalIdsRef.current.add(externalContactId);

      contactsService
        .list({ ...scope, search: externalContactId }, { page: 0, size: 50 })
        .then((res) => {
          const exact = (res.content ?? []).find(
            (contact) => contact.externalId?.trim() === externalContactId,
          );
          if (!exact) return;
          setContactsPool((prev) => mergeById(prev, [exact]));
        })
        .catch(() => {
          hydratedOwnerExternalIdsRef.current.delete(externalContactId);
        });
    });
  }, [open, data, ownerContactIds, contactsPool, legalEntityId, branchId]);

  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.name?.trim() || b.id })),
    [branches],
  );

  const negotiatorOptions = useMemo(
    () =>
      negotiators
        .map((u) => ({
          value: u.id,
          label: u.name?.trim() || u.email?.trim() || u.externalId?.trim() || u.id,
        }))
        .filter((o) => o.value),
    [negotiators],
  );

  const handleOwnerChange = (index: number, contactId: string) => {
    setOwnerContactIds((prev) => {
      const next = [...prev];
      next[index] = contactId;
      return next;
    });
    if (contactId && !contactsPool.some((c) => c.id === contactId)) {
      contactsService
        .getOne(contactId, legalEntityId ?? undefined)
        .then((raw) => {
          setContactsPool((prev) =>
            mergeById(prev, [contactDetailsToSummary(raw, contactId)]),
          );
        })
        .catch(() => undefined);
    }
  };

  const onSubmit = async (values: PropertyFormValues) => {
    if (!propertyId || !loadedRef.current) return;

    const validation = validatePropertyForm(values, ownerContactIds, { isCreate: false });
    if (!validation.success) {
      toast.error(validation.message);
      return;
    }

    setIsSaving(true);
    try {
      const patch = buildPropertyUpdatePatch(values);
      const updated = await propertiesService.update(propertyId, patch);
      loadedRef.current = updated;
      const owners = ownerIdsFromDetail(updated);
      baselineOwnersRef.current = owners.filter(Boolean).join("|");
      setOwnerContactIds(owners);
      reset(propertyDetailToFormValues(updated, negotiators));
      toast.success("Property updated successfully", {
        toastId: "property-update-success",
      });
      onSaved?.();
      reload();
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to update property");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOwnerCreated = (created: ContactSummary) => {
    setContactsPool((prev) => mergeById(prev, [created]));
    setOwnerContactIds((prev) => {
      const emptyIdx = prev.findIndex((id) => !id.trim());
      if (emptyIdx >= 0) {
        const next = [...prev];
        next[emptyIdx] = created.id;
        return next;
      }
      return [...prev, created.id];
    });
    setOwnerCreateOpen(false);
  };

  const listingDetails = useMemo(
    () => (data ? propertyListingReadOnlyFromDetail(data) : null),
    [data],
  );

  const mediaGalleryItems = useMemo<GalleryImageViewItem[]>(() => {
    const items: GalleryImageViewItem[] = [];

    mediaAssets.forEach((asset, assetIndex) => {
      const assetId = asset.id ?? asset.externalId ?? `asset-${assetIndex}`;
      const gallery = Array.isArray(asset.galleryImages) ? asset.galleryImages : [];

      if (gallery.length === 0) {
        const fallbackUrl =
          asString(asset.internalThumbnailUrl) ??
          asString(asset.internalUrl);
        if (fallbackUrl) {
          items.push({
            id: `${assetId}-fallback`,
            order: typeof asset.order === "number" ? asset.order : null,
            isMain: false,
            modifiedAt: asString(asset.modified),
            thumbnailUrl: fallbackUrl,
            screenUrl: fallbackUrl,
          });
        }
        return;
      }

      gallery.forEach((raw, imageIndex) => {
        const image = (raw ?? {}) as Record<string, unknown>;
        const thumbnailUrl =
          asString(image.internalSizesThumbnailSrc) ??
          asString(image.internalSizesTileSrc) ??
          asString(image.internalSizesScreenSrc) ??
          asString(asset.internalThumbnailUrl) ??
          asString(asset.internalUrl);

        if (!thumbnailUrl) return;

        const screenUrl =
          asString(image.internalSizesScreenSrc) ??
          thumbnailUrl;

        items.push({
          id: `${assetId}-${asString(image.externalId) ?? imageIndex}`,
          order: asNumber(image.order) ?? asNumber(asset.order),
          isMain: image.main === true,
          modifiedAt: asString(image.modifiedDate) ?? asString(asset.modified),
          thumbnailUrl,
          screenUrl,
        });
      });
    });

    return items.sort((a, b) => {
      const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.id.localeCompare(b.id);
    });
  }, [mediaAssets]);

  const mediaViewerImages = useMemo(
    () =>
      mediaGalleryItems.map((item) => ({
        id: item.id,
        src: item.screenUrl,
        alt: item.order != null ? `Order ${item.order}` : "Property media",
      })),
    [mediaGalleryItems],
  );

  const drawerTitle = data
    ? propertyDisplayLabel(data).slice(0, 80)
    : "Property";

  // Prefer the detail body so a refetch wins, but it omits `syncAlto` today —
  // in practice the list row's value is what shows.
  const effectiveCrmSyncStatus = data?.crmSyncStatus ?? data?.syncAlto ?? crmSyncStatus ?? syncAlto;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={drawerTitle}
        subtitle={propertyId ? (
          <span className="group/copy inline-flex items-center gap-1">
            <span>ID: {propertyId}</span>
            <CopyButton
              value={propertyId}
              label="Property ID"
              successMessage="Record ID copied to clipboard"
            />
          </span>
        ) : undefined}
        width="2xl"
        actions={
          <div className="flex items-center gap-2">
            {effectiveCrmSyncStatus === "IN_PROGRESS" && (
              <span className="mr-2 flex items-center gap-2 text-sm text-bridge-muted">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
                </span>
                {crmDisplayName(data?.sourceCrm)} sync in progress...
              </span>
            )}
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            {data && activeTab === "Overview" ? (
              <Button
                variant="primary"
                loading={isSaving}
                disabled={!hasChanges || isSaving || effectiveCrmSyncStatus === "IN_PROGRESS"}
                onClick={handleSubmit(onSubmit)}
              >
                Update property
              </Button>
            ) : null}
          </div>
        }
      >
        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        )}
        {error && (
          <div className="text-sm text-bridge-danger">{error.message}</div>
        )}
        {data && (
          <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row md:gap-6">
            <aside className="flex shrink-0 flex-row gap-1 overflow-x-auto rounded-2xl border border-bridge-border/50 bg-white/80 p-2 shadow-sm md:sticky md:top-0 md:w-48 md:self-start md:flex-col md:overflow-visible lg:w-52">
              {PROPERTY_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "whitespace-nowrap rounded-xl px-3 py-2 text-left text-sm transition-colors md:px-4",
                    activeTab === tab
                      ? "bg-[#009BF5] font-medium text-white shadow-sm ring-1 ring-[#009BF5]/20"
                      : "text-bridge-muted hover:bg-bridge-surface-hover hover:text-bridge-fg",
                  )}
                >
                  {tab}
                </button>
              ))}
            </aside>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1" onScroll={handleContentScroll}>
              {activeTab === "Overview" ? (
                <div className="space-y-4">
                  {data?.crmSyncStatus === "FAILED" && data?.crmSyncError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50/90 p-4 shadow-sm">
                      <div className="flex gap-3">
                        <svg className="h-5 w-5 shrink-0 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <h3 className="text-sm font-medium text-red-800">CRM Sync Failed</h3>
                          <p className="mt-1 text-sm text-red-700">{friendlyCrmSyncError(data.crmSyncError)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <form
                    className="space-y-4 rounded-2xl border border-bridge-border/40 bg-white/95 p-4 shadow-sm sm:p-6"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <PropertyFormFields
                      isCreate={false}
                      listingDetails={listingDetails}
                      register={register}
                      watch={watch}
                      setValue={setValue}
                      branchOptions={branchOptions}
                      negotiatorOptions={negotiatorOptions}
                      loadingNegotiators={resolvingNegotiator || loadingNegotiators}
                      onNegotiatorOpen={handleNegotiatorOpen}
                      onNegotiatorEndReached={handleNegotiatorEndReached}
                      ownerContactIds={ownerContactIds}
                      ownerOptions={ownerOptions}
                      loadingContacts={loadingContacts}
                      onOwnerChange={handleOwnerChange}
                      onAddOwner={() =>
                        setOwnerContactIds((prev) => [...prev, ""])
                      }
                      onRemoveOwner={(index) =>
                        setOwnerContactIds((prev) =>
                          prev.length <= 1
                            ? prev
                            : prev.filter((_, i) => i !== index),
                        )
                      }
                      onContactSearch={fetchContacts}
                      onCreateOwnerClick={() => setOwnerCreateOpen(true)}
                    />
                  </form>
                </div>
              ) : activeTab === "Media" ? (
                <div className="rounded-2xl border border-bridge-border/40 bg-white p-4 shadow-sm sm:p-6">
                  {mediaLoading && mediaGalleryItems.length === 0 ? (
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                      {Array.from({ length: 8 }).map((_, idx) => (
                        <Skeleton key={idx} className="aspect-[4/3] w-full" />
                      ))}
                    </div>
                  ) : mediaError ? (
                    <p className="text-sm text-bridge-danger">{mediaError}</p>
                  ) : mediaGalleryItems.length === 0 ? (
                    <p className="text-sm text-bridge-muted">
                      No images available for this property.
                    </p>
                  ) : (
                    <>
                      <p className="mb-3 text-xs text-bridge-muted">
                        {mediaGalleryItems.length} image
                        {mediaGalleryItems.length === 1 ? "" : "s"} loaded from
                        media assets.
                      </p>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                        {mediaGalleryItems.map((item, idx) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setMediaViewerIndex(idx)}
                            className="group relative overflow-hidden rounded-md border border-bridge-border/60 bg-bridge-surface/60 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009BF5]"
                            title="Open full image"
                          >
                            <img
                              src={item.thumbnailUrl}
                              alt="Property media"
                              loading="lazy"
                              className="aspect-[4/3] h-full w-full object-cover transition-transform group-hover:scale-105"
                            />
                            {item.isMain ? (
                              <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-xs bg-black/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white">
                                <Star size={10} /> Main
                              </span>
                            ) : null}
                            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-[10px] text-white/90">
                              {item.order != null ? `Order ${item.order}` : "Gallery image"}
                            </span>
                          </button>
                        ))}
                      </div>
                      {mediaLoadingMore ? (
                        <p className="mt-3 text-center text-xs text-bridge-muted">
                          Loading more images...
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ) : activeTab === "Documents" ? (
                <div className="rounded-2xl border border-bridge-border/40 bg-white p-4 shadow-sm sm:p-6">
                  {!data.externalId?.trim() ? (
                    <p className="text-sm text-bridge-muted">
                      This property has no external ID, so linked documents cannot be loaded.
                    </p>
                  ) : documentsError ? (
                    <p className="text-sm text-bridge-danger">{documentsError}</p>
                  ) : (
                    <>
                      {!documentsLoading && documents.length === 0 ? (
                        <EmptyState
                          title="No documents"
                          description="No documents are linked to this property yet."
                        />
                      ) : (
                        <>
                          {!documentsLoading && documents.length > 0 ? (
                            <p className="mb-3 text-xs text-bridge-muted">
                              {documents.length} document{documents.length === 1 ? "" : "s"} for
                              this property.
                            </p>
                          ) : null}
                          <DataTable
                            rows={documents}
                            columns={PROPERTY_DOCUMENT_COLUMNS}
                            loading={documentsLoading}
                            rowKey={(r) => r.id}
                            emptyTitle="No documents"
                            emptyDescription="No documents are linked to this property yet."
                          />
                          {documentsLoadingMore ? (
                            <p className="mt-3 text-center text-xs text-bridge-muted">
                              Loading more documents...
                            </p>
                          ) : null}
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : activeTab === "Timeline" ? (
                <div className="rounded-2xl border border-bridge-border/40 bg-white p-4 shadow-sm sm:p-6">
                  {propertyId ? (
                    // Temporarily hidden — re-enable File Note on property Timeline when ready.
                    // <button
                    //   type="button"
                    //   onClick={() => setFileNoteOpen(true)}
                    //   className="text-sm font-medium text-bridge-accent underline decoration-bridge-accent/50 underline-offset-2 transition-colors hover:text-bridge-accent/90 hover:decoration-bridge-accent focus-ring rounded-sm"
                    // >
                    //   File Note
                    // </button>
                    <p className="text-sm text-bridge-muted">
                      Timeline will be displayed here.
                    </p>
                  ) : (
                    <p className="text-sm text-bridge-muted">
                      Timeline will be displayed here.
                    </p>
                  )}
                </div>
              // ) : activeTab === "Availability" ? (
              //   propertyId ? (
              //     <PropertyAvailabilityTab
              //       property={{
              //         id: propertyId,
              //         sourceCrm: data?.sourceCrm ?? null,
              //         externalId: data?.externalId ?? null,
              //         displayAddress: data?.displayAddress ?? null,
              //         legalEntityId: legalEntityId ?? null,
              //       }}
              //     />
              //   ) : (
              //     <div className="flex min-h-[200px] items-center justify-center rounded-md border border-bridge-border/40 bg-white p-6 text-sm text-bridge-muted">
              //       Select a property to manage availability.
              //     </div>
              //   )
              ) : (
                <div className="flex min-h-[200px] items-center justify-center rounded-md border border-bridge-border/40 bg-white p-6 text-sm text-bridge-muted">
                  {activeTab} module will be loaded here.
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <ImageViewer
        open={mediaViewerIndex !== null}
        images={mediaViewerImages}
        index={mediaViewerIndex ?? 0}
        onIndexChange={setMediaViewerIndex}
        onClose={() => setMediaViewerIndex(null)}
      />

      <ContactDetailDrawer
        open={ownerCreateOpen}
        contactId={null}
        onClose={() => setOwnerCreateOpen(false)}
        onUpdate={() => {}}
        onCreate={handleOwnerCreated}
      />

      {/* <FileNoteModal
        open={fileNoteOpen}
        onClose={() => setFileNoteOpen(false)}
        propertyId={propertyId}
      /> */}
    </>
  );
}
